import * as Yup from "yup";
import { validationsLocale } from "./ValidationsLocale";

/**
 * Sets the Yup validation locale using predefined messages.
 * Ensures all validation errors follow Portuguese (Portugal) localization.
 */
Yup.setLocale(validationsLocale);

/**
 * Centralized validation messages (in pt-PT).
 * Used consistently across Yup schema definitions to maintain consistency and allow easy updates.
 */
const MESSAGES = {
  required: "Este campo é de preenchimento obrigatório.",
  initialInvestmentRequired: 'O campo "Subscrição inicial" é obrigatório.',
  initialInvestmentTypeError:
    'O campo "Subscrição inicial" deve ser um número inteiro.',
  initialInvestmentPositive:
    'O campo "Subscrição inicial" deve ser um número positivo.',
  initialInvestmentMin:
    'O valor do "Subscrição inicial" deve ser de pelo menos 100€.',
  additionalSubscriptionTypeRequired:
    'O campo "Tipo de subscrições adicionais" é obrigatório.',
  additionalSubscriptionTypeInvalid:
    'O tipo de subscrição deve ser: "monthly", "annual" ou "none".',
  monthlySubscriptionTypeError:
    'O campo "Subscrições mensais" deve ser um número.',
  monthlySubscriptionPositive:
    'O campo "Subscrições mensais" deve ser um número positivo.',
  monthlySubscriptionMin: "O valor mínimo das subscrições mensais é €10.",
  quarterlySubscriptionTypeError:
    'O campo "Subscrições trimestrais" deve ser um número.',
  quarterlySubscriptionPositive:
    'O campo "Subscrições trimestrais" deve ser um número positivo.',
  quarterlySubscriptionMin: "O valor mínimo das subscrições trimestrais é €10.",
  annualSubscriptionTypeError:
    'O campo "Subscrições anuais" deve ser um número.',
  annualSubscriptionPositive:
    'O campo "Subscrições anuais" deve ser um número positivo.',
  annualSubscriptionMin: "O valor mínimo das subscrições anuais é €10.",
  termYearsRequired: 'O campo "Prazo" é obrigatório.',
  termYearsInvalid: "O prazo deve ser entre 1 e 15 anos.",
  interestRateRequired: 'O campo "Taxa de juro" é obrigatório.',
  interestRateTypeError: 'O campo "Taxa de juro" deve ser um número.',
  interestRateMin: 'O campo "Taxa de juro" deve ser no mínimo 0.',
  interestRateMax: 'O campo "Taxa de juro" deve ser no máximo 2,5.',
  interestRateDecimals: "A taxa de juro não pode ter mais de 3 casas decimais.",
  interestTaxationRequired:
    'O campo "Tributação sobre os juros" é obrigatório.',
  interestTaxationInvalid:
    'A tributação deve ser: "portugal_continental", "azores" ou "madeira".',
  totalContributionMax:
    "A soma da subscrição inicial e das subscrições ao longo do prazo não pode exceder €250.000.",
};

/**
 * Yup validation schemas for the Savings Certificates simulation form.
 * Ensures that all inputs respect domain-specific rules.
 *
 * Schema: `savings_certificates`
 *
 * Fields:
 * - initial_investment: required, numeric, ≥ 100, multiple of 100
 * - additional_subscription_type: required, oneOf ["monthly", "annual", "none"]
 * - monthly_subscription_amount: required if type === monthly, ≥ 10
 * - annual_subscription_amount: required if type === annual, ≥ 10
 * - term_years: required, oneOf ["1", "2", "3", "4", "5"]
 * - interest_rate: required, numeric, positive, ≤ 20, max 3 decimal places
 * - interest_taxation: required, oneOf ["portugal_continental", "azores", "madeira"]
 */
export const validations: Record<string, Yup.ObjectSchema<any>> = {
  savings_certificates: Yup.object().shape({
    initial_investment: Yup.number()
      .required(MESSAGES.initialInvestmentRequired)
      .typeError(MESSAGES.initialInvestmentTypeError)
      .positive(MESSAGES.initialInvestmentPositive)
      .integer(MESSAGES.initialInvestmentTypeError) // only integers
      .transform((value, originalValue) => {
        if (typeof originalValue === "string") {
          const clean = originalValue.replace(/[.,]/g, "");
          return clean === "" ? NaN : Number(clean);
        }
        return originalValue;
      })
      .min(100, MESSAGES.initialInvestmentMin)
      .max(250000, MESSAGES.totalContributionMax),

    additional_subscription_type: Yup.string()
      .required(MESSAGES.additionalSubscriptionTypeRequired)
      .oneOf(
        ["monthly", "quarterly", "annual", "none"],
        MESSAGES.additionalSubscriptionTypeInvalid,
      ),

    monthly_subscription_amount: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        // reject if decimal part exists
        if (/[.,]\d{1,}$/.test(originalValue)) {
          return NaN;
        }
        return Number(originalValue.replace(/[.,]/g, ''));
      }
      return value;
    })
    .when(
      "additional_subscription_type",
      {
        is: "monthly",
        then: (schema) =>
          schema
            .required(MESSAGES.monthlySubscriptionTypeError)
            .typeError(MESSAGES.monthlySubscriptionTypeError)
            .positive(MESSAGES.monthlySubscriptionPositive)
            .integer(MESSAGES.monthlySubscriptionTypeError) // only integers
            .min(10, MESSAGES.monthlySubscriptionMin),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    quarterly_subscription_amount: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        // reject if decimal part exists
        if (/[.,]\d{1,}$/.test(originalValue)) {
          return NaN;
        }
        return Number(originalValue.replace(/[.,]/g, ''));
      }
      return value;
    })
    .when(
      "additional_subscription_type",
      {
        is: "quarterly",
        then: (schema) =>
          schema
            .required(MESSAGES.quarterlySubscriptionTypeError)
            .typeError(MESSAGES.quarterlySubscriptionTypeError)
            .positive(MESSAGES.quarterlySubscriptionPositive)
            .integer(MESSAGES.quarterlySubscriptionTypeError) // only integers
            .min(10, MESSAGES.quarterlySubscriptionMin),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    annual_subscription_amount: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        // reject if decimal part exists
        if (/[.,]\d{1,}$/.test(originalValue)) {
          return NaN;
        }
        return Number(originalValue.replace(/[.,]/g, ''));
      }
      return value;
    })
    .when(
      "additional_subscription_type",
      {
        is: "annual",
        then: (schema) =>
          schema
            .required(MESSAGES.annualSubscriptionTypeError)
            .typeError(MESSAGES.annualSubscriptionTypeError)
            .positive(MESSAGES.annualSubscriptionPositive)
            .integer(MESSAGES.annualSubscriptionTypeError) // only integers
            .min(10, MESSAGES.annualSubscriptionMin),
        otherwise: (schema) => schema.notRequired(),
      },
    ),

    term_years: Yup.string()
      .required(MESSAGES.termYearsRequired)
      .oneOf(
        [
          "1",
          "2",
          "3",
          "4",
          "5",
          "6",
          "7",
          "8",
          "9",
          "10",
          "11",
          "12",
          "13",
          "14",
          "15",
        ],
        MESSAGES.termYearsInvalid,
      ),

    interest_rate: Yup.number()
      .required(MESSAGES.interestRateRequired)
      .typeError(MESSAGES.interestRateTypeError)
      .min(0, MESSAGES.interestRateMin)
      .max(2.5, MESSAGES.interestRateMax)
      .test(
        "max-decimals",
        MESSAGES.interestRateDecimals,
        (value) => {
          if (value === undefined || value === null) return true;
          const str = value.toString();
          const decimals = str.includes(".") ? str.split(".")[1].length : 0;
          return decimals <= 3;
        },
      ),

    interest_taxation: Yup.string()
      .required(MESSAGES.interestTaxationRequired)
      .oneOf(
        ["portugal_continental", "azores", "madeira"],
        MESSAGES.interestTaxationInvalid,
      ),
  })
    .test(
      "total-contribution-max",
      MESSAGES.totalContributionMax,
      function (values) {
        if (!values) return true;

        const term = parseInt(values.term_years, 10);
        const type = values.additional_subscription_type;

        // If required fields aren’t valid yet, don’t block with this error.
        if (!Number.isFinite(term) || !values.initial_investment) return true;

        const init = Number(values.initial_investment) || 0;

        let recurring = 0;
        let path = "initial_investment";

        switch (type) {
          case "monthly":
            recurring =
              (Number(values.monthly_subscription_amount) || 0) * 12 * term;
            path = "monthly_subscription_amount";
            break;
          case "quarterly":
            recurring =
              (Number(values.quarterly_subscription_amount) || 0) * 4 * term;
            path = "quarterly_subscription_amount";
            break;
          case "annual":
            recurring =
              (Number(values.annual_subscription_amount) || 0) * term;
            path = "annual_subscription_amount";
            break;
          default:
            recurring = 0;
            path = "initial_investment";
        }

        const total = init + recurring;
        if (total > 250000) {
          return this.createError({
            path,
            message: MESSAGES.totalContributionMax,
          });
        }
        return true;
      },
    ),
};
